import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { fetchApi } from '@/lib/api/client'
import { populationKeys } from '@/lib/population/query-keys'
import type {
  Breakdown,
  BreakdownDimension,
  DimensionKind,
  LmsBucket,
  PhishingBucket,
  ReportDimension,
} from '@/lib/population/types'

/**
 * Report dimensions and the two breakdowns (lane B2 contract §4, §6).
 *
 * Every number on the breakdown screens is the API's: counts and rates come
 * from SQL, a suppressed bucket keeps its counts and has every rate null, and
 * the total row exists only when the API sends one. This file only coerces
 * the wire shape into the declared types; it computes nothing.
 */

const KINDS: readonly DimensionKind[] = [
  'department',
  'designation',
  'location',
  'country',
  'manager',
  'tag',
  'custom',
  'category',
]

type Rec = Record<string, unknown>
const asRec = (raw: unknown): Rec => (raw && typeof raw === 'object' ? (raw as Rec) : {})
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
const rate = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const text = (v: unknown): string | null => (typeof v === 'string' ? v : null)
const kindOf = (v: unknown): DimensionKind =>
  KINDS.includes(v as DimensionKind) ? (v as DimensionKind) : 'custom'

function listOf(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw
  const data = asRec(raw).data
  return Array.isArray(data) ? data : []
}

export function normalizeDimension(raw: unknown): ReportDimension {
  const r = asRec(raw)
  return {
    id: String(r.id ?? ''),
    kind: kindOf(r.kind),
    label: text(r.label),
    labelKey: text(r.labelKey),
    exclusive: r.exclusive === true,
    bucketCount: num(r.bucketCount),
  }
}

export function normalizeDimensions(raw: unknown): ReportDimension[] {
  return listOf(raw)
    .map(normalizeDimension)
    .filter((d) => d.id !== '')
}

function normalizeBreakdownDimension(raw: unknown): BreakdownDimension {
  const { id, kind, label, labelKey, exclusive } = normalizeDimension(raw)
  return { id, kind, label, labelKey, exclusive }
}

/** A suppressed bucket never carries a rate, whatever the wire says. */
function suppressedOf(r: Rec): boolean {
  return r.suppressed === true
}

export function normalizePhishingBucket(raw: unknown): PhishingBucket {
  const r = asRec(raw)
  const suppressed = suppressedOf(r)
  return {
    key: text(r.key),
    label: text(r.label),
    people: num(r.people),
    sent: num(r.sent),
    opened: num(r.opened),
    clicked: num(r.clicked),
    compromised: num(r.compromised),
    reported: num(r.reported),
    openRate: suppressed ? null : rate(r.openRate),
    clickRate: suppressed ? null : rate(r.clickRate),
    compromiseRate: suppressed ? null : rate(r.compromiseRate),
    reportRate: suppressed ? null : rate(r.reportRate),
    suppressed,
  }
}

export function normalizeLmsBucket(raw: unknown): LmsBucket {
  const r = asRec(raw)
  const suppressed = suppressedOf(r)
  return {
    key: text(r.key),
    label: text(r.label),
    people: num(r.people),
    assigned: num(r.assigned),
    completed: num(r.completed),
    overdue: num(r.overdue),
    completionRate: suppressed ? null : rate(r.completionRate),
    suppressed,
  }
}

export function normalizeBreakdown<B>(raw: unknown, bucket: (raw: unknown) => B): Breakdown<B> {
  const r = asRec(raw)
  return {
    dimension: normalizeBreakdownDimension(r.dimension),
    privacyFloor: typeof r.privacyFloor === 'number' ? r.privacyFloor : 5,
    buckets: Array.isArray(r.buckets) ? r.buckets.map(bucket) : [],
    total: r.total && typeof r.total === 'object' ? bucket(r.total) : null,
  }
}

export function useReportDimensions() {
  return useQuery({
    queryKey: populationKeys.dimensions.list(),
    queryFn: () => fetchApi<unknown>('/reports/dimensions'),
    select: normalizeDimensions,
  })
}

/** `qs` is the whole query, built by breakdownQuery() (dimension + report filters + population). */
export function usePhishingBreakdown(qs: string, enabled = true) {
  return useQuery({
    queryKey: populationKeys.breakdowns.phishing(qs),
    queryFn: () => fetchApi<unknown>(`/phishing/reports/breakdown?${qs}`),
    select: (raw) => normalizeBreakdown(raw, normalizePhishingBucket),
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
  })
}

export function useLmsBreakdown(qs: string, enabled = true) {
  return useQuery({
    queryKey: populationKeys.breakdowns.lms(qs),
    queryFn: () => fetchApi<unknown>(`/lms/reports/breakdown?${qs}`),
    select: (raw) => normalizeBreakdown(raw, normalizeLmsBucket),
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
  })
}
